import { useMemo } from 'react';

import { MannWhitneyResultsV2, MannWhitneyResultsDataV2 } from 'api/data/analyses';
import { Table } from 'components/UI/Table';
import { useTranslation } from 'hooks/store';
import { parseAnalysisNumber } from 'store/data/analyses/parsers';
import { AnalysisStatistic } from 'components/Analysis/Analyses';
import { useAnalysisErrorMessage } from 'hooks/store/data/analysis/useAnalysisErrorMessage';

interface Props {
	results: MannWhitneyResultsV2;
}

export function CompareNumericMannWhitney({ results }: Props) {
	const { translate } = useTranslation();

	const { data, error } = results;

	const translatedMessage = useAnalysisErrorMessage(error);

	return (
		<AnalysisStatistic>
			<AnalysisStatistic.Title>
				{translate(({ analysis }) => analysis.statistics.mannWhitney.name)}
			</AnalysisStatistic.Title>

			{error && (
				<AnalysisStatistic.Error>
					{translatedMessage ??
						translate(({ errors }) => errors.api.analyses.statistics.mannWhitney)}
				</AnalysisStatistic.Error>
			)}

			{data && <Result data={data} />}
		</AnalysisStatistic>
	);
}

function Result({ data }: { data: MannWhitneyResultsDataV2 }) {
	const { uStatistic, pStatistic } = useMemo(() => {
		const uStatistic = getUStatistic({
			statistic: data.statistic
		});

		const pStatistic = getPStatistic({
			pValue: data.pValue
		});

		return { uStatistic, pStatistic };
	}, [data]);

	return (
		<Table.Responsive horizontalScroll fullWidth={false}>
			<Table>
				<Table.Body>
					<Table.Row>
						<Table.Cell title={uStatistic.text} width={14} noWrap>
							{uStatistic.JSX}
						</Table.Cell>
						<Table.Cell title={pStatistic.text} width={14} noWrap>
							{pStatistic.JSX}
						</Table.Cell>
					</Table.Row>
				</Table.Body>
			</Table>
		</Table.Responsive>
	);
}

function getUStatistic(input: { statistic: number }) {
	const { statistic } = input;

	const { tenExponent, value: parsedStatistic } = parseAnalysisNumber(statistic);

	const getTenExponentText = () => {
		if (tenExponent !== 0) {
			if (tenExponent < 0) {
				return `*10^(${tenExponent})`;
			}
			return `*10^${tenExponent}`;
		}
		return '';
	};

	const output = {
		JSX: (
			<>
				<i>{`U`}</i>
				{` = ${parsedStatistic}`}
				{tenExponent !== 0 && (
					<>
						*10<sup style={{ lineHeight: 1, alignSelf: 'unset' }}>{tenExponent}</sup>
					</>
				)}
			</>
		),
		text: `U = ${parsedStatistic}${getTenExponentText()}`
	};

	return output;
}

function getPStatistic(input: { pValue: number }) {
	const { pValue } = input;

	const { operator, value: parsedPValue } = parseAnalysisNumber(pValue, {
		decimals: 3,
		formatAsPValue: true
	});

	const output = {
		JSX: (
			<>
				<i>{`p`}</i>
				{` ${operator} ${parsedPValue}`}
			</>
		),
		text: `p ${operator} ${parsedPValue}`
	};

	return output;
}
